import { useAxiosQuery } from 'hooks/useAxiosQuery';
import { useGetBlueprintPowerProfile } from 'features/blueprints/hooks/useGetBlueprintPowerProfile';
import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { BLUEPRINT_POWER_PROFILE_CACHE_KEY } from 'services/queryKeys';

export interface RecommendedSetup {
  batteryMinKw: number;
  batteryMinKwh: number;
  gensetMinKva: number;
}

interface UseGetEquipmentRecommendationsParams {
  gridSizeAmperes?: number;
  powerProfile?: string;
  blueprintId?: number; // For fetching power profile from API in edit mode
  mode?: 'create' | 'edit'; // Mode to determine if we should fetch from API
}

export const useGetEquipmentRecommendations = ({
  gridSizeAmperes,
  powerProfile,
  blueprintId,
  mode = 'create',
}: UseGetEquipmentRecommendationsParams) => {
  const queryClient = useQueryClient();

  const { isLoading: isPowerProfileLoading } = useGetBlueprintPowerProfile({
    blueprintId: blueprintId?.toString() || '',
    enabled: mode === 'edit' && !!blueprintId && !powerProfile,
  });

  // Get raw CSV from react-query cache if available
  const powerProfileCsvFromApi = queryClient.getQueryData<string>(
    BLUEPRINT_POWER_PROFILE_CACHE_KEY(blueprintId?.toString() || '')
  );

  // Use powerProfile from form if available, otherwise use API data in edit mode
  const finalPowerProfile = powerProfile || (mode === 'edit' ? powerProfileCsvFromApi : undefined);

  const formData = useMemo(() => {
    if (!finalPowerProfile) {
      return null;
    }
    const formDataToSend = new FormData();
    const csvBlob = new Blob([finalPowerProfile], { type: 'text/csv' });
    formDataToSend.append('power_profile', csvBlob, 'power-profile.csv');
    return formDataToSend;
  }, [finalPowerProfile]);

  const gridSizeAmperesValue = gridSizeAmperes ?? 0;

  const { data, isLoading: isRecommendationsLoading, ...rest } = useAxiosQuery<RecommendedSetup>(
    ['equipment-recommendations', gridSizeAmperesValue.toString(), finalPowerProfile || ''],
    `/blueprints/recommendation?grid-size-amperes=${gridSizeAmperesValue}`,
    {
      method: 'POST',
      body: formData,
      enabled: !!finalPowerProfile,
    }
  );

  return {
    data,
    isLoading: isRecommendationsLoading || isPowerProfileLoading,
    ...rest,
  };
};
